import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SyncState } from '../constants/matrixEvents';
import { MatrixContext } from './MatrixContext';
import { useI18n } from './I18nContext';
import { useLoading } from './LoadingContext';
import { useToast } from './ToastContext';
import matrixService from '../services/matrixService';
import draftStorageService from '../services/draftStorageService';
import { initInactiveDeletionConfig } from '../services/serverConfigService';
import logger from '../utils/logger';

/**
 * Provides a fully initialized and synced Matrix client to the application.
 *
 * This lean provider only provides the client instance - all state management
 * is handled by dedicated hooks for better separation of concerns.
 */
export const MatrixClientProvider = ({ children }) => {
  const { t } = useI18n();
  const { showLoading, hideLoading } = useLoading();
  const { showError } = useToast();
  const [client, setClient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Listen for crypto initialization failures
  useEffect(() => {
    const handleCryptoDisabled = (event) => {
      showError(
        t('encryptionUnavailable.title'),
        t('encryptionUnavailable.message'),
      );
      logger.warn('Crypto disabled event received:', event.detail);
    };

    window.addEventListener('matrix-crypto-disabled', handleCryptoDisabled);
    return () => window.removeEventListener('matrix-crypto-disabled', handleCryptoDisabled);
  }, [showError, t]);

  useEffect(() => {
    let isMounted = true;
    let syncListener = null;
    logger.info('MatrixClientProvider: Initializing client service...');

    const initializeClient = async () => {
      try {
        // Show loading overlay during initial sync
        showLoading();
        
        // Initialize the Matrix service (which handles all the setup)
        await matrixService.initialize();
        
        // Start sync
        await matrixService.startSync();
        
        // Get the client instance
        const initializedClient = matrixService.getClient();
        
        if (isMounted && initializedClient) {
          logger.info('MatrixClientProvider: Client initialized, waiting for initial sync...');
          setClient(initializedClient);
          
          // Listen for sync state changes to hide loading when room data is ready
          syncListener = (state) => {
            logger.info('MatrixClientProvider: Sync state changed to:', state);
            
            // Hide loading when we reach Prepared state (room list is loaded)
            if (state === SyncState.Prepared) {
              logger.info('MatrixClientProvider: Initial sync prepared, room data loaded.');
              setIsLoading(false);
              hideLoading();
              
              // Cleanup old drafts (>7 days)
              const cleanedCount = draftStorageService.cleanupOldDrafts();
              if (cleanedCount > 0) {
                logger.info(`Cleaned up ${cleanedCount} old draft(s)`);
              }

              // Kick off (silent) Synapse server-config fetch. The result is
              // cached for the session; failure silently falls back to 180 days
              // and never reaches the user. The chat-expiry hook also awaits
              // this promise, so this call is only here to start it earlier.
              initInactiveDeletionConfig().catch(() => { /* swallowed by service */ });
            }
          };
          
          initializedClient.on('sync', syncListener);
        }
      } catch (err) {
        logger.error('MatrixClientProvider: Failed to initialize Matrix client:', err);
        if (isMounted) {
          setError(err);
          setIsLoading(false);
          hideLoading();
        }
      }
    };

    initializeClient();

    return () => {
      isMounted = false;
      // Clean up sync listener
      const currentClient = matrixService.getClient();
      if (currentClient && syncListener) {
        currentClient.off('sync', syncListener);
      }
      // Note: We don't stop the client here as it might be needed for logout
      // The client lifecycle is managed by matrixService
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Initialization should only run once on mount

  if (isLoading) {
    return null; // Loading overlay is shown via LoadingContext
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
      }}>
        <h2 style={{ color: '#d32f2f', marginBottom: '10px' }}>
          Failed to Initialize Session
        </h2>
        <p style={{ color: '#666', maxWidth: '500px' }}>
          {error.message}
        </p>
        <p style={{ color: '#999', marginTop: '20px' }}>
          Please try refreshing the page or logging in again.
        </p>
      </div>
    );
  }

  return (
    <MatrixContext.Provider value={client}>
      {children}
    </MatrixContext.Provider>
  );
};

MatrixClientProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
